import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../../services/api';
import './DateSelector.scss';

const DateSelector = ({ onDateSelect, selectedDate, location, practitioner, timeZone, onTimeZoneChange }) => {
  const [availableDates, setAvailableDates] = useState([]);
  const [dateAvailability, setDateAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTimeZone, setSelectedTimeZone] = useState(timeZone || '(GMT+08:00) Perth');

  const timeZones = [
    '(GMT+08:00) Perth',
    '(GMT+10:00) Sydney',
    '(GMT+10:00) Melbourne',
    '(GMT+09:30) Adelaide',
    '(GMT+09:30) Darwin',
    '(GMT+10:00) Brisbane',
    '(GMT+08:00) Western Australia',
  ];

  useEffect(() => {
    generateAvailableDates();
  }, []);

  useEffect(() => {
    if (availableDates.length > 0) {
      checkAvailabilityForDates();
    }
  }, [location, practitioner, availableDates]);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    // Generate next 60 days
    for (let i = 1; i <= 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip Sundays (day 0)
      if (date.getDay() !== 0) {
        dates.push(date);
      }
    }
    
    setAvailableDates(dates);
    setLoading(false);
  };

  const checkAvailabilityForDates = async () => {
    const availability = {};
    
    // Check first 10 dates for availability periods
    const datesToCheck = availableDates.slice(0, 10);
    
    for (const date of datesToCheck) {
      try {
        const dateStr = formatDate(date);
        const response = await bookingAPI.getAvailableSlots(dateStr, location?._id, practitioner?._id);
        
        if (response.data.groupedByPeriod) {
          const periods = response.data.groupedByPeriod;
          availability[dateStr] = {
            morning: periods.morning?.length > 0,
            afternoon: periods.afternoon?.length > 0,
            evening: periods.evening?.length > 0,
          };
        }
      } catch (err) {
        console.error(`Error checking availability for ${formatDate(date)}:`, err);
      }
    }
    
    setDateAvailability(availability);
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!selectedDate) return false;
    return formatDate(date) === formatDate(new Date(selectedDate));
  };

  const getAvailabilityIndicators = (date) => {
    const dateStr = formatDate(date);
    const avail = dateAvailability[dateStr];
    
    if (!avail) {
      return { morning: false, afternoon: false, evening: false };
    }
    
    return avail;
  };

  const handleDateClick = (date) => {
    onDateSelect(formatDate(date));
  };

  const handleTimeZoneChange = (e) => {
    const newTimeZone = e.target.value;
    setSelectedTimeZone(newTimeZone);
    if (onTimeZoneChange) {
      onTimeZoneChange(newTimeZone);
    }
  };

  if (loading) {
    return <div className="loading-state">Loading available dates...</div>;
  }

  return (
    <div className="date-selector">
      <div className="date-selector-header">
        <div>
          <h2>Select a Date</h2>
          <p className="subtitle">Choose your preferred appointment date</p>
        </div>
        <div className="timezone-selector">
          <label htmlFor="timezone">Time Zone:</label>
          <select
            id="timezone"
            value={selectedTimeZone}
            onChange={handleTimeZoneChange}
            className="timezone-dropdown"
          >
            {timeZones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="availability-legend">
        <span className="legend-item">
          <span className="indicator morning"></span>
          <span>Morning (AM)</span>
        </span>
        <span className="legend-item">
          <span className="indicator afternoon"></span>
          <span>Afternoon (PM)</span>
        </span>
        <span className="legend-item">
          <span className="indicator evening"></span>
          <span>Evening (PM)</span>
        </span>
      </div>
      
      <div className="calendar-grid">
        {availableDates.map((date, index) => {
          const indicators = getAvailabilityIndicators(date);
          return (
            <button
              key={index}
              className={`date-card ${isSelected(date) ? 'selected' : ''} ${isToday(date) ? 'today' : ''}`}
              onClick={() => handleDateClick(date)}
            >
              <div className="date-info">
                <div className="date-day">{formatDisplayDate(date).split(',')[0]}</div>
                <div className="date-month">{formatDisplayDate(date).split(',')[1].trim().split(' ')[0]}</div>
                <div className="date-number">{date.getDate()}</div>
              </div>
              <div className="availability-indicators">
                {indicators.morning && <span className="indicator morning" title="Morning available"></span>}
                {indicators.afternoon && <span className="indicator afternoon" title="Afternoon available"></span>}
                {indicators.evening && <span className="indicator evening" title="Evening available"></span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DateSelector;
